<template>
  <BaseModal
    :show="show"
    @update:show="$emit('update:show', $event)"
    @close="$emit('close')"
    title="Welcome to PopMates"
    size="md"
    :show-footer="false"
  >
    <!-- Modal Content -->
    <div class="space-y-6">
      <!-- Tabs for Login/Signup -->
      <div class="flex space-x-4 border-b">
        <button
          @click="activeTab = 'login'"
          :class="[
            'pb-2 px-1 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'login'
              ? 'text-blue-600 border-blue-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          ]"
        >
          Sign In
        </button>
        <button
          @click="activeTab = 'signup'"
          :class="[
            'pb-2 px-1 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'signup'
              ? 'text-blue-600 border-blue-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          ]"
        >
          Create Account
        </button>
      </div>

      <!-- Success Message for Signup -->
      <div v-if="showSignupSuccess && !loading" class="py-8">
        <div class="text-green-600 mb-4">
          <svg class="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 text-center mb-2">
          Account Created Successfully!
        </h3>
        <p class="text-sm text-gray-600 text-center mb-4">
          We've sent a confirmation email to your inbox.
          Please click the link in the email to activate your account.
        </p>
        <p class="text-xs text-gray-500 text-center">
          <strong>Note:</strong> Check your spam folder if you don't see the email within a few minutes.
        </p>
        <button
          @click="showSignupSuccess = false; activeTab = 'login'"
          class="mt-4 w-full px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          Go to Sign In
        </button>
      </div>

      <!-- Loading State -->
      <div v-else-if="loading || userStore.loading" class="py-8">
        <BaseSpinner size="lg" class="mx-auto" />
        <p class="text-gray-600 mt-4 text-center">
          {{ activeTab === 'login' ? 'Signing you in...' : 'Creating your account...' }}
        </p>
      </div>

      <!-- Login Form -->
      <form v-else-if="activeTab === 'login'" @submit.prevent="handleLogin" class="space-y-4">
        <div>
          <label for="login-email" class="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            id="login-email"
            v-model="loginForm.email"
            type="email"
            required
            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <div class="flex items-center justify-between">
            <label for="login-password" class="block text-sm font-medium text-gray-700">
              Password
            </label>
            <router-link
              to="/forgot-password"
              class="text-xs font-medium text-blue-600 hover:text-blue-800"
              @click="$emit('update:show', false)"
            >
              Forgot password?
            </router-link>
          </div>
          <input
            id="login-password"
            v-model="loginForm.password"
            type="password"
            required
            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="••••••••"
          />
        </div>

        <!-- Error Message -->
        <div v-if="error || userStore.error" class="p-3 bg-red-50 border border-red-200 rounded">
          <p class="text-sm text-red-700">
            {{ error || userStore.error }}
          </p>
        </div>

        <BaseButton
          type="submit"
          variant="primary"
          size="lg"
          :loading="loading"
          :disabled="!loginForm.email || !loginForm.password"
          fullWidth
          class="justify-center"
        >
          Sign In
        </BaseButton>
      </form>

      <!-- Signup Form -->
      <form v-else-if="activeTab === 'signup'" @submit.prevent="handleSignup" class="space-y-4">
        <div>
          <label for="signup-email" class="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            id="signup-email"
            v-model="signupForm.email"
            type="email"
            required
            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label for="signup-password" class="block text-sm font-medium text-gray-700">
            Password
          </label>
          <input
            id="signup-password"
            v-model="signupForm.password"
            type="password"
            required
            minlength="6"
            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="At least 6 characters"
          />
        </div>

        <div>
          <label for="signup-confirm-password" class="block text-sm font-medium text-gray-700">
            Confirm Password
          </label>
          <input
            id="signup-confirm-password"
            v-model="signupForm.confirmPassword"
            type="password"
            required
            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="••••••••"
          />
        </div>

        <!-- Error Message -->
        <div v-if="error || userStore.error" class="p-3 bg-red-50 border border-red-200 rounded">
          <p class="text-sm text-red-700">
            {{ error || userStore.error }}
          </p>
        </div>

        <BaseButton
          type="submit"
          variant="primary"
          size="lg"
          :loading="loading"
          :disabled="!signupForm.email || !signupForm.password || !signupForm.confirmPassword"
          fullWidth
          class="justify-center"
        >
          Create Account
        </BaseButton>

        <div class="p-3 bg-blue-50 border border-blue-200 rounded">
          <p class="text-xs text-blue-700 text-center">
            <strong>📧 Email Confirmation Required</strong>
          </p>
          <p class="text-xs text-blue-600 text-center mt-1">
            After signing up, check your email for a confirmation link to activate your account.
          </p>
        </div>
      </form>

      <!-- Terms and Privacy -->
      <div class="pt-4 border-t border-gray-200">
        <p class="text-xs text-gray-500 text-center">
          By continuing, you agree to our
          <a href="#" class="text-blue-600 hover:text-blue-800">Terms of Service</a>
          and
          <a href="#" class="text-blue-600 hover:text-blue-800">Privacy Policy</a>
        </p>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { BaseModal, BaseButton, BaseSpinner } from '@/components/ui'

interface Props {
  show: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  close: []
  success: []
}>()

const router = useRouter()
const userStore = useUserStore()

const activeTab = ref<'login' | 'signup'>('login')
const loading = ref(false)
const error = ref<string | null>(null)
const showSignupSuccess = ref(false)

const loginForm = reactive({
  email: '',
  password: ''
})

const signupForm = reactive({
  email: '',
  password: '',
  confirmPassword: ''
})

const handleLogin = async () => {
  try {
    loading.value = true
    error.value = null

    await userStore.signIn(loginForm.email, loginForm.password)

    // Show success state briefly before redirecting
    emit('success')

    // Close modal
    emit('update:show', false)
    emit('close')

    // Redirect to main app
    setTimeout(() => {
      router.push('/app')
    }, 500)

  } catch (err) {
    console.error('Login error:', err)
    error.value = err instanceof Error ? err.message : 'Login failed'
  } finally {
    loading.value = false
  }
}

const handleSignup = async () => {
  try {
    loading.value = true
    error.value = null

    // Validate passwords match
    if (signupForm.password !== signupForm.confirmPassword) {
      throw new Error('Passwords do not match')
    }

    // Validate password length
    if (signupForm.password.length < 6) {
      throw new Error('Password must be at least 6 characters')
    }

    await userStore.signUp(signupForm.email, signupForm.password)

    // Check if user is authenticated after signup (auto-confirm enabled)
    if (userStore.isAuthenticated) {
      // User was auto-confirmed, proceed normally
      emit('success')
      emit('update:show', false)
      emit('close')

      setTimeout(() => {
        router.push('/app')
      }, 500)
    } else {
      // Email confirmation required - try to sign them in automatically
      console.log('Email confirmation may be required, attempting auto-signin...')

      try {
        // Attempt to sign in with the same credentials
        await userStore.signIn(signupForm.email, signupForm.password)

        if (userStore.isAuthenticated) {
          emit('success')
          emit('update:show', false)
          emit('close')

          setTimeout(() => {
            router.push('/app')
          }, 500)
        } else {
          // Still can't sign in - email confirmation definitely required
          error.value = 'Account created! Please check your email to confirm your account before signing in.'
          // Switch to login tab so user can try signing in after confirming email
          activeTab.value = 'login'
          loginForm.email = signupForm.email
          loginForm.password = signupForm.password
        }
      } catch (signInError) {
        // Sign in failed - email confirmation definitely required
        // Show success message instead of error since account was created successfully
        showSignupSuccess.value = true
        error.value = null
        // Clear the signup form
        signupForm.email = ''
        signupForm.password = ''
        signupForm.confirmPassword = ''
      }
    }

  } catch (err) {
    console.error('Signup error:', err)
    error.value = err instanceof Error ? err.message : 'Signup failed'
  } finally {
    loading.value = false
  }
}

const clearErrors = () => {
  error.value = null
  userStore.clearError()
}

const resetForms = () => {
  loginForm.email = ''
  loginForm.password = ''
  signupForm.email = ''
  signupForm.password = ''
  signupForm.confirmPassword = ''
  showSignupSuccess.value = false
  clearErrors()
}

// Watch for successful authentication from userStore
watch(() => userStore.isAuthenticated, (isAuthenticated) => {
  // Skip if we're in the middle of submitting to avoid duplicate events
  if (loading.value) {
    return
  }

  if (isAuthenticated && props.show) {
    // User is now authenticated, close modal and redirect
    emit('update:show', false)
    emit('close')
    emit('success')

    setTimeout(() => {
      router.push('/app')
    }, 500)
  }
})

// Clear errors and reset forms when modal is closed
watch(() => props.show, (newShow) => {
  if (!newShow) {
    resetForms()
    activeTab.value = 'login'
  }
})
</script>